import { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  DataGrid,
  DatePicker,
  messageFromError,
  toast,
  unwrapAction,
  useGridConfig,
  useGridRows,
  todayIso,
} from '@eo/shared';

import type { OverviewCfo } from './api/types';
import { makeOverviewGridConfig } from './config/gridConfig';
import type { OverviewTabProps } from './types';

export function OverviewTab({ date, overview, probes, onVerify }: OverviewTabProps) {
  const [, setSearchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  const onDateChange = (value: string) => {
    if (!value) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('date', value);
      return next;
    });
  };

  // The grid config is memoized on first render, so its cells read the date and
  // the probe results through getters rather than closing over the first ones.
  const refs = useRef({ date, probes, verifying });
  refs.current = { date, probes, verifying };

  const currentDate = useCallback(() => refs.current.date, []);
  const probeFor = useCallback((userId: string) => refs.current.probes[userId] ?? null, []);
  const isVerifying = useCallback(() => refs.current.verifying, []);

  const runVerify = useCallback(
    (userId?: string) => {
      if (refs.current.verifying) return;
      void (async () => {
        setVerifying(true);
        try {
          unwrapAction(await onVerify({ userId }));
          toast.success(userId ? 'Token checked.' : 'Every stored token checked.');
        } catch (err) {
          toast.error(messageFromError(err, 'Checking against ClickUp failed. Try again.'));
        } finally {
          setVerifying(false);
        }
      })();
    },
    [onVerify],
  );

  const onVerifyRow = useCallback((userId: string) => runVerify(userId), [runVerify]);

  const config = useGridConfig(makeOverviewGridConfig, {
    date: currentDate,
    probeFor,
    onVerify: onVerifyRow,
    isVerifying,
  });
  const rows = useGridRows(overview, config);

  return (
    <section className="flex flex-col gap-3">
      <p className="text-sm text-text-tertiary">
        Stored credentials and review activity per CFO on the selected date. Credential state comes
        from the database; <strong>Verify</strong> checks a ClickUp token against ClickUp, which
        costs one request per token.
      </p>
      <DataGrid<OverviewCfo>
        config={config}
        rows={rows.rows}
        pagination={rows.pagination}
        headerActions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={verifying} onClick={() => runVerify()}>
              {verifying ? 'Checking…' : 'Verify all'}
            </Button>
            <DatePicker
              value={date}
              onChange={onDateChange}
              ariaLabel="Overview date"
              maxDate={todayIso()}
              className="w-40"
            />
          </div>
        }
      />
    </section>
  );
}
